import { memo } from "react";
import { CheckCircle2, Download, FileWarning } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { pluralize } from "@/shared/lib/formatters";
import { formatPeriodLabel } from "../constants";
import type { FiscalExportResult as ExportResult } from "../types/fiscal-export.types";

interface FiscalExportResultProps {
  result: ExportResult;
  startDate: string;
  endDate: string;
  onDownloadAgain: () => void;
}

export const FiscalExportResult = memo(function FiscalExportResult({
  result,
  startDate,
  endDate,
  onDownloadAgain,
}: FiscalExportResultProps) {
  const total = result.nfseCount + result.nfceCount;

  return (
    <div className="space-y-4 rounded-lg border border-primary/30 bg-primary/5 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">
              Arquivo gerado com {pluralize(total, "nota", "notas")}
            </p>
            <p className="text-sm text-muted-foreground">
              Período de {formatPeriodLabel(startDate, endDate)} ·{" "}
              {pluralize(result.nfseCount, "nota fiscal", "notas fiscais")} e{" "}
              {pluralize(
                result.nfceCount,
                "nota comercial",
                "notas comerciais",
              )}
              .
            </p>
            <p className="font-mono text-xs text-muted-foreground/80">
              {result.filename}
            </p>
          </div>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={onDownloadAgain}
          className="gap-2 self-start"
        >
          <Download className="h-4 w-4" />
          Baixar de novo
        </Button>
      </div>

      {result.skippedCount > 0 && (
        <div className="flex items-start gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
          <FileWarning className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div className="space-y-1 text-sm">
            <p className="font-medium text-foreground">
              {pluralize(result.skippedCount, "nota ficou", "notas ficaram")} de
              fora do arquivo
            </p>
            <p className="text-muted-foreground">
              {result.skippedCount === 1 ? "Ela está" : "Elas estão"} no
              período, mas o XML ainda não foi baixado da prefeitura ou da
              SEFAZ. Abra a nota em Notas Fiscais ou Notas Comerciais e use a
              opção de baixar os documentos novamente — depois disso, gere o
              arquivo outra vez.
            </p>
          </div>
        </div>
      )}
    </div>
  );
});
